import { TableRow as MuiTableRow } from '@mui/material';
import cx from 'classnames';
import type React from 'react';
import { useCallback } from 'react';
import styled from 'styled-components';
import { TextCell } from './table-cells';
import type { TableColumn, TableColumns, TableData } from './types';

const TableRowContainer = styled(MuiTableRow)`
  &.clickable {
    cursor: pointer;
  }
`;

export type TableRowProps = {
  readonly index: number;
  readonly columns: TableColumns;
  readonly data: TableData;
  readonly onRowClick?: (data: TableData) => void;
  readonly tableName: string;
  readonly idFieldName?: string;
  readonly disabledFieldName?: string;
  readonly disabled?: boolean;
  readonly selected?: boolean;
};

const TableRow: React.FC<TableRowProps> = (props: TableRowProps) => {
  const { data, columns, onRowClick, tableName, idFieldName, disabledFieldName, selected } = props;

  const handleClick = useCallback(() => {
    if (onRowClick) {
      onRowClick(data);
    }
  }, [data, onRowClick]);

  const classNames = cx({
    clickable: onRowClick,
    selected
  });

  const cells = columns.map((column: TableColumn | string) => {
    let name = '';
    if (typeof column === 'string') {
      name = column;
    }

    if (typeof column === 'object') {
      if (column.component) {
        return (
          <column.component
            idFieldName={idFieldName}
            disabledFieldName={disabledFieldName}
            tableName={tableName}
            row={data}
            value={data[column.name]}
            key={column.name}
            column={column}
          />
        );
      }

      name = column.name;
    }

    return (
      <TextCell key={name} title={data[name]}>
        {data[name]}
      </TextCell>
    );
  });

  return (
    <TableRowContainer
      data-testid="table-row"
      onClick={handleClick}
      className={classNames}
      hover={onRowClick !== undefined}
      selected={selected}
    >
      {cells}
    </TableRowContainer>
  );
};

export default TableRow;
